import { ChangeEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../components/button'
import { BackButton } from '../../../components/button/backButton'
import { Status } from '../../../components/cards/statusTag'
import { ImageBanner } from '../../../components/images/imageBanner'
import { AddCategoryModal } from '../../../components/modal/app_modals/addCategoryModal'
import { ImageUploadPreviewModal } from '../../../components/modal/app_modals/ImagePreviewModal'
import { getCategorySectionLabel } from '../../../components/product/category-section'
import { LoadingOval } from '../../../components/spinner/Spinner'
import { PRIMARY_COLOR } from '../../../constants'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useEditProductCategoryMutation,
  useGetSingleCategoryQuery,
} from '../../../redux-toolkits/products/products.slice'
import { SingleSubCategory } from '../../../redux-toolkits/products/products.type'
import SubCategories from '../subCategories/categorySubCategories'

export const SingleCategoryView = ({
  cat,
  onSubCategoryClick,
}: {
  cat: string
  onSubCategoryClick: (subCat: SingleSubCategory) => void
}) => {
  const [uploadedIcon, setUploadedIcon] = useState<File>()

  const navigate = useNavigate()

  const { modal, modalOpen, handleModalClose, handleModalOpen, setModalOpen } =
    useModalControl()

  const [editCategory] = useEditProductCategoryMutation()

  const { data: category, isFetching: fetchingCategory } =
    useGetSingleCategoryQuery(
      {
        categoryId: cat,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    )

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }
    const selectedFile = event.target.files[0]
    setUploadedIcon(selectedFile)
    handleModalOpen('imageUploadPreviewModal')
  }
  return fetchingCategory ? (
    <div className="w-full flex items-center justify-center">
      <LoadingOval color={PRIMARY_COLOR} />
    </div>
  ) : (
    <div className="flex flex-col gap-6">
      <BackButton
        text="All categories"
        onClick={() => {
          navigate('/products?tab=Categories')
        }}
      />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-2 flex-1 max-w-[600px]">
          <ImageBanner
            imageUrl={category?.data?.icon ?? ''}
            loading={false}
            className="
            !rounded-full
            !w-[80px] !max-h-[80px]
            !bg-contain
            "
            errors={''}
            onImageEdit={(e) => {
              handleFileChange(e)
            }}
          />

          <p className="text-2xl font-recoleta font-semibold">
            {category?.data?.name}
          </p>

          <p className="text-sec-black text-base">
            {category?.data?.description}
          </p>

          <p className="text-sm text-sec-black">
            Section:{' '}
            <span className="font-medium text-black">
              {getCategorySectionLabel(category?.data?.section)}
            </span>
          </p>

          <Status
            text={category?.data?.isActive ? 'Active' : 'Inactive'}
            type={category?.data?.isActive ? 'success' : 'fail'}
          />
        </div>

        <Button
          type="button"
          onClick={() => {
            handleModalOpen('editCategoryModal')
          }}
          primary
          className="w-full max-w-[220px]"
          label="Manage Category"
          size="medium"
        />
      </div>

      <p className="text-xl font-semibold">Subcategories</p>

      <SubCategories
        categoryId={category?.data?._id ?? ''}
        onSubCategoryClick={onSubCategoryClick}
      />

      <AddCategoryModal
        open={modalOpen && modal === 'editCategoryModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={(cat) => {
          handleModalClose()
        }}
        mode={'edit'}
        category={category?.data}
      />

      {uploadedIcon ? (
        <ImageUploadPreviewModal
          image={uploadedIcon as File}
          open={modal === 'imageUploadPreviewModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            setUploadedIcon(undefined)
          }}
          onSuccessCallback={async (url) => {
            if (category?.data?._id) {
              await editCategory({
                id: category.data._id,
                payload: {
                  icon: url,
                  name: category?.data?.name,
                  description: category?.data?.description,
                  comboTagLine: category?.data?.comboTagLine,
                  recommendedTagLine: category?.data?.recommendedTagLine,
                },
              })
            }
          }}
        />
      ) : null}
    </div>
  )
}
